import React, { createContext, useContext, useState, useEffect } from 'react';
import { useDocuments } from './DocumentsContext';

// Create a context for document types
const DocumentTypesContext = createContext();

// Hook for accessing document types
export const useDocumentTypes = () => {
  const context = useContext(DocumentTypesContext);
  if (!context) {
    throw new Error('useDocumentTypes must be used within a DocumentTypesProvider');
  }
  return context;
};

export const DocumentTypesProvider = ({ children }) => {
  // Document types state
  const [documentTypes, setDocumentTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Get documents context for fetching data
  const { documents } = useDocuments();
  
  // Standard document types - these are the core types used across the application
  // IMPORTANT: Keep these in sync with the document types in DocumentCreation.js
  const standardDocumentTypes = [
    // Purchase related - only include types that are in DocumentCreation.js
    { id: 'purchase-order', name: 'Bon de commande', category: 'purchase', code: 'BC' },
    { id: 'reception-note', name: 'Bon de réception', category: 'purchase', code: 'BR' },
    
    // Sales related - only include types that are in DocumentCreation.js
    { id: 'invoice', name: 'Facture de vente', category: 'sales', code: 'FAC' },
    { id: 'delivery-note', name: 'Bon de livraison', category: 'sales', code: 'BL' },
    // { id: 'quote', name: 'Devis', category: 'sales', code: 'DEV' }, // Removed as it's not in DocumentCreation.js
    
    // Stock related
    { id: 'stock-sheet', name: 'Fiche de stock', category: 'stock', code: 'FS' },
    { id: 'material-exit', name: 'Bon de sortie', category: 'stock', code: 'BS' },
    
    // Other
    { id: 'credit-note', name: "Avoir", category: 'other', code: 'AVO' },
    { id: 'payment-receipt', name: 'Reçu', category: 'other', code: 'REC' }
  ];
  
  // Load and sync document types - use documents from DocumentsContext
  useEffect(() => {
    const loadDocumentTypes = async () => {
      setIsLoading(true);
      try {
        // Extract document types from documents in DocumentsContext
        const extractedTypes = [];
        
        // First add standard document types
        setDocumentTypes(standardDocumentTypes);
        
        // Check if we have documents from DocumentsContext
        if (documents && documents.length > 0) {
          console.log('Extracting document types from documents:', documents.length);
          
          // Create a set to track unique document types we've already seen
          const existingTypeIds = new Set(standardDocumentTypes.map(t => t.id));
          
          // Extract unique document types from existing documents
          documents.forEach(doc => {
            if (doc.type && !existingTypeIds.has(doc.type)) {
              // Extract a new document type
              existingTypeIds.add(doc.type);
              
              // Get prefix from document number if possible
              let code = '';
              if (doc.number && doc.number.includes('-')) {
                code = doc.number.split('-')[0];
              }
              
              // Determine category based on document properties
              let category = 'other';
              if (doc.supplierId) category = 'purchase';
              else if (doc.clientId) category = 'sales';
              
              // Create readable name
              let name = doc.type.split('-').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ');
              
              extractedTypes.push({
                id: doc.type,
                name: name,
                category: category,
                code: code || doc.type.substring(0, 3).toUpperCase()
              });
            }
          });
          
          // Update document types with both standard and extracted types
          if (extractedTypes.length > 0) {
            setDocumentTypes([...standardDocumentTypes, ...extractedTypes]);
            console.log('Added custom document types:', extractedTypes);
          }
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error('Error loading document types:', err);
        setError('Failed to load document types');
        setIsLoading(false);
      }
    };
    
    loadDocumentTypes();
  }, [documents, standardDocumentTypes]);
  
  // Get document types filtered by category
  const getDocumentTypesByCategory = (category) => {
    // First check if we have document types from the documents page
    const categoryTypes = documentTypes.filter(type => type.category === category);
    
    // For purchase category, ensure we only return types that match what's in DocumentCreation.js
    if (category === 'purchase') {
      // These are the only purchase document types that should be available
      const validPurchaseTypes = ['purchase-order', 'reception-note'];
      return categoryTypes.filter(type => validPurchaseTypes.includes(type.id));
    }
    
    // For sales category, ensure we only return types that match what's in DocumentCreation.js
    if (category === 'sales') {
      // These are the only sales document types that should be available
      const validSalesTypes = ['invoice', 'delivery-note'];
      return categoryTypes.filter(type => validSalesTypes.includes(type.id));
    }
    
    return categoryTypes;
  };
  
  // Get document type by ID
  const getDocumentTypeById = (id) => {
    return documentTypes.find(type => type.id === id);
  };
  
  // Get document type by code
  const getDocumentTypeByCode = (code) => {
    return documentTypes.find(type => type.code === code);
  };
  
  // Generate a document number based on type
  const generateDocumentNumber = (typeId) => {
    const docType = getDocumentTypeById(typeId);
    if (!docType) return 'DOC-001';
    
    const prefix = docType.code;
    const date = new Date();
    const year = date.getFullYear().toString().substr(2, 2);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const counter = Math.floor(Math.random() * 999) + 1;
    
    return `${prefix}-${year}${month}-${counter.toString().padStart(3, '0')}`;
  };
  
  // Context value
  const value = {
    documentTypes,
    isLoading,
    error,
    getDocumentTypesByCategory,
    getDocumentTypeById,
    getDocumentTypeByCode,
    generateDocumentNumber
  };
  
  return (
    <DocumentTypesContext.Provider value={value}>
      {children}
    </DocumentTypesContext.Provider>
  );
};

export default DocumentTypesContext;